import { AdminRoles } from '@bp/pim-auth-constants';
import { Button, Input } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useMemo } from 'react';
import { CombinedError, useMutation, useQuery } from 'urql';
import {
  PimCreateOrganizationDocument,
  PimCreateOrganizationMutation,
  PimCreateOrganizationMutationVariables,
  PimListOrganizationsDocument,
  PimListOrganizationsQuery,
  PimListOrganizationsQueryVariables,
  PimUpdateOrganizationsDocument,
  PimUpdateOrganizationsMutation,
  PimUpdateOrganizationsMutationVariables,
} from '../../../client/graphql-client-defs';
import { PropertiesArrayField } from '../../Properties/PropertiesForm/PropertiesArrayField';
import { ListPlainOrganizationsType } from '../graphql/types';

interface OrganisationFormProps {
  uuid?: string;
  afterSubmit: () => void;
}

export const OrganizationForm = ({ uuid, afterSubmit }: OrganisationFormProps) => {
  const context = useMemo(() => ({ additionalTypenames: ['Organization'] }), []);
  const [, createOrganization] = useMutation<PimCreateOrganizationMutation, PimCreateOrganizationMutationVariables>(
    PimCreateOrganizationDocument,
  );
  const [, updateOrganization] = useMutation<PimUpdateOrganizationsMutation, PimUpdateOrganizationsMutationVariables>(
    PimUpdateOrganizationsDocument,
  );
  const [{ data, fetching }] = useQuery<PimListOrganizationsQuery, PimListOrganizationsQueryVariables>({
    query: PimListOrganizationsDocument,
    context,
    variables: {
      where: {
        uuid: uuid,
      },
      options: {
        limit: 1,
      },
    },
    pause: !uuid,
  });
  const initialValues: ListPlainOrganizationsType = {
    __typename: 'Organization',
    locale: 'de',
    timezone: 'Europe/Berlin',
    name: '',
    uuid: '',
    tecartContactId: '',
    properties: [],
    groupsConnection: { __typename: 'OrganizationGroupsConnection', totalCount: 0 },
    profilesConnection: { __typename: 'OrganizationProfilesConnection', totalCount: 0 },
    propertiesConnection: { __typename: 'OrganizationPropertiesConnection', edges: [], totalCount: 0 },
    shadowsProfilesToConnection: { edges: [] },
    licensedApplicationsConnection: { edges: [] },
  };

  const handleSubmit = async (
    values: ListPlainOrganizationsType,
    formHelpers: FormikHelpers<ListPlainOrganizationsType>,
  ) => {
    if (values) {
      const { uuid, __typename, ...rest } = values;

      let mutationError: CombinedError | undefined;

      if (uuid === '') {
        let { data: createOrgaData, error } = await createOrganization({
          input: {
            name: rest.name,
            timezone: rest.timezone,
            locale: rest.locale,
            tecartContactId: rest.tecartContactId,
            profiles: {
              create: [
                {
                  node: {
                    firstName: 'Admin',
                    lastName: 'TK-Schulsoftware',
                    email: 'admin@tks.eu',
                    user: { connect: { where: { node: { email: 'admin@tks.eu' } } } },
                    incomingRoles: {
                      Organization: {
                        connect: [
                          { where: { node: { name: rest.name } }, edge: { names: [AdminRoles.OrganizationAdmin] } },
                        ],
                      },
                    },
                  },
                },
              ],
            },
          },
        });
        mutationError = error;
        if (createOrgaData?.createOrganizations?.organizations[0].uuid) {
          await updateOrganization({
            where: { uuid: createOrgaData?.createOrganizations?.organizations[0].uuid },
            update: {
              incomingRoles: {
                Profile: [
                  {
                    connect: [
                      { edge: { names: [AdminRoles.OrganizationAdmin] }, where: { node: { email: 'admin@tks.eu' } } },
                    ],
                  },
                ],
              },
            },
          });
        }
      } else {
        const { error } = await updateOrganization({
          update: {
            name: rest.name,
            timezone: rest.timezone,
            locale: rest.locale,
            tecartContactId: rest.tecartContactId,
          },
          where: {
            uuid: uuid,
          },
        });
        mutationError = error;
      }
      if (!mutationError) {
        afterSubmit();
        formHelpers.resetForm();
      }
    }
  };

  const organization: ListPlainOrganizationsType = data && data.organizations ? data.organizations[0] : initialValues;
  return (
    <>
      <Formik onSubmit={handleSubmit} initialValues={organization}>
        {({ handleChange, values, isSubmitting, errors }) => {
          return (
            <Form>
              <div className={'form-block'}>
                <div className={'form-group'}>
                  <div className={'form-col'}>
                    <div className={'form-row'}>
                      <Input
                        name={'name'}
                        className={'full'}
                        value={values.name}
                        label={'Name'}
                        onChange={handleChange}
                      />
                    </div>
                    <div className={'form-row'}>
                      <Input
                        name={'tecartContactId'}
                        className={'full'}
                        value={values.tecartContactId}
                        label={'Tecart Contact ID'}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <PropertiesArrayField />

              <div className={'modal-bottom'}>
                <Button
                  type='submit'
                  disabled={isSubmitting || fetching || (errors && Object.values(errors).length > 0)}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
